/* eslint no-console: 0 */
import {normalizeText} from "./amendments/normalize_text";
import {
  MAX_SEARCH_LENGTH,
  searchInDocument,
  searchRange,
} from "./amendments/search_in_document";
import {searchWithProgressiveExpansion} from "./amendments/search_with_progressive_expansion";
import {expandToAdjacentParagraph} from "./amendments/expand_to_adjacent_paragraph";
import {insertPlainText} from "./amendments/insert_plain_text";
import {DocumentAmendment} from "common/flowmaster/types/amendment";
import {romanToInt} from "common/utils/roman_number";
import {
  stripClauseNumberPrefix,
  hasClauseNumberPrefix,
  extractLastReferenceComponent,
  formatNumberPrefix,
} from "./amendments/clause_number_utils";

// Phrase-match widening limits, relative to target length
const TARGET_COVERAGE_THRESHOLD = 0.8;
const MAX_EXPANSION_RATIO = 1.2;

interface AnchorPairResult {
  beforeRange: Word.Range;
  afterRange: Word.Range;
  betweenRange: Word.Range;
  distance: number;
}

export interface AmendmentError {
  amendmentIndex: number;
  message: string;
  amendment: DocumentAmendment;
  isPartial?: boolean;
  actionString?: string;
}

export interface SubAmendmentFailure {
  amendment: DocumentAmendment;
  reason: string;
}
export interface ProcessAmendmentStatus {
  status: "success" | "partial";
  failures?: SubAmendmentFailure[];
}

async function processAmendment(
  context: Word.RequestContext,
  amendment: DocumentAmendment,
  log: (str: string) => void,
): Promise<ProcessAmendmentStatus> {
  if (isClauseRenumberReplace(amendment)) {
    const autoNumbered = await isRenumberTargetAutoNumbered(
      context,
      amendment,
      log,
    );
    if (autoNumbered) {
      log(
        `Skipping renumber-only amendment "${amendment.deleted_text}" -> ` +
          `"${amendment.added_text}": clause is auto-numbered, Word renumbers it automatically`,
      );
      return {status: "success"};
    }
  }

  if (amendment.subAmendments) {
    return await applyMultiAmendment(context, amendment, log);
  }

  await applySingleAmendment(context, amendment, log);
  return {status: "success"};
}

const ACTION_ORDER: Record<string, number> = {
  delete: 1,
  delete_clause: 1,
  replace: 2,
  insert_inline: 3,
  insert_before: 4,
  insert_after: 5,
};

const DIGIT_PATTERN = /^\d+$/;
const ROMAN_PATTERN = /^[ivxlcdm]+$/;
const LETTER_PATTERN = /^[a-z]$/;

export function parseClauseReference(ref: string | undefined): string[] {
  if (!ref) {
    return [];
  }
  // Drop a "part/" prefix echoed from the working set, e.g. "SPECIFICATION/1.3"
  const bare = ref.slice(ref.lastIndexOf("/") + 1);
  const tokens = bare.match(/\([^)]+\)|[^.()]+/g) || [];
  return tokens.map(token => token.replace(/[()]/g, "").toLowerCase());
}

function isClauseReferenceText(text: string): boolean {
  const tokens = parseClauseReference(text.trim());
  return (
    tokens.length >= 1 &&
    tokens.every(
      token =>
        DIGIT_PATTERN.test(token) ||
        LETTER_PATTERN.test(token) ||
        ROMAN_PATTERN.test(token),
    )
  );
}

function normalizeClauseReference(text: string): string {
  return parseClauseReference(text.trim()).join(".");
}

export function isClauseRenumberReplace(amendment: DocumentAmendment): boolean {
  if (amendment.action_type !== "replace") {
    return false;
  }
  const deleted = (amendment.deleted_text ?? "").trim();
  const added = (amendment.added_text ?? "").trim();
  if (deleted === added) {
    return false;
  }
  if (!isClauseReferenceText(deleted)) {
    return false;
  }

  if (added !== "" && !isClauseReferenceText(added)) {
    return false;
  }
  return (
    normalizeClauseReference(amendment.clause_reference ?? "") ===
    normalizeClauseReference(deleted)
  );
}

function compareSegments(a: string, b: string): number {
  if (DIGIT_PATTERN.test(a) && DIGIT_PATTERN.test(b)) {
    return parseInt(a, 10) - parseInt(b, 10);
  }
  if (
    ROMAN_PATTERN.test(a) &&
    ROMAN_PATTERN.test(b) &&
    (a.length > 1 || b.length > 1)
  ) {
    return romanToInt(a) - romanToInt(b);
  }
  if (LETTER_PATTERN.test(a) && LETTER_PATTERN.test(b)) {
    return a.charCodeAt(0) - b.charCodeAt(0);
  }
  return a < b ? -1 : a > b ? 1 : 0;
}

export function compareReferenceSegments(a: string[], b: string[]): number {
  const length = Math.max(a.length, b.length);
  for (let i = 0; i < length; i++) {
    const segmentA = a[i] ?? "";
    const segmentB = b[i] ?? "";
    if (segmentA !== segmentB) {
      const comparison = compareSegments(segmentA, segmentB);
      if (comparison !== 0) {
        return comparison;
      }
    }
  }
  return 0;
}

type DecoratedAmendment = {
  amendment: DocumentAmendment;
  originalIndex: number;
  isInsert: boolean;
  referenceSegments: string[];
};

function decorateAmendment(
  amendment: DocumentAmendment,
  originalIndex: number,
): DecoratedAmendment {
  const isInsert =
    amendment.action_type === "insert_after" ||
    amendment.action_type === "insert_before";
  const reference = isInsert
    ? amendment.target_clause_reference || amendment.clause_reference
    : amendment.clause_reference;
  return {
    amendment,
    originalIndex,
    isInsert,
    referenceSegments: parseClauseReference(reference),
  };
}

function compareLegacy(a: DecoratedAmendment, b: DecoratedAmendment): number {
  if (b.amendment.section_idx !== a.amendment.section_idx) {
    return (b.amendment.section_idx ?? 0) - (a.amendment.section_idx ?? 0);
  }
  return (
    ACTION_ORDER[a.amendment.action_type] -
    ACTION_ORDER[b.amendment.action_type]
  );
}

function compareByReference(
  a: DecoratedAmendment,
  b: DecoratedAmendment,
): number {
  if (a.isInsert !== b.isInsert) {
    return a.isInsert ? 1 : -1;
  }
  const direction = a.isInsert ? 1 : -1;
  const comparison =
    compareReferenceSegments(a.referenceSegments, b.referenceSegments) ||
    a.originalIndex - b.originalIndex;
  return comparison * direction;
}

export function sortAmendments(amendments: DocumentAmendment[]) {
  const useLegacy = amendments.some(a => a.section_idx !== undefined);
  const comparator = useLegacy ? compareLegacy : compareByReference;
  return amendments.map(decorateAmendment).sort(comparator);
}

export async function applyDocumentAmendments(
  amendments: DocumentAmendment[],
  onAmendmentApplyFail: (
    amendment: DocumentAmendment,
    errorMessage?: string,
  ) => void,
  onAmendmentApplyPartialFail: (
    amendment: DocumentAmendment,
    failures: SubAmendmentFailure[],
  ) => void,
  onAmendmentApplySuccess: (amendment: DocumentAmendment) => void,
): Promise<{
  errors: AmendmentError[];
  completedAmendments: DocumentAmendment[];
}> {
  return await window.Word.run(async (context: Word.RequestContext) => {
    const errors: AmendmentError[] = [];
    const completedAmendments: DocumentAmendment[] = [];

    const sortedAmendments = sortAmendments(amendments);

    for (const {amendment, originalIndex} of sortedAmendments) {
      const clause =
        amendment.clause_reference ??
        (amendment.instruction ?? amendment.rationale ?? "").match(
          /clause ([0-9]+(.?[0-9]+)*)/i,
        )?.[0];
      const log = str =>
        console.log(`[Amendment ${originalIndex} Clause ${clause}] ${str}`);
      const actionString = `${amendment.action_type} ${clause ?? ""}`;
      console.log(
        `== Processing amendment ${originalIndex} (${actionString}) ==`,
      );
      console.groupCollapsed(
        "%cAmendment object",
        "font-weight: normal; color: inherit;",
      );
      console.dir(amendment);
      console.groupEnd();
      try {
        const result = await processAmendment(context, amendment, log);
        if (result.status === "success") {
          completedAmendments.push(amendment);
          onAmendmentApplySuccess(amendment);
          console.log(
            `== Successfully processed amendment ${originalIndex} ==`,
          );
        }
        if (result.status === "partial") {
          if (result.failures === undefined) {
            throw new Error(
              "Process multi amendment returned with status partial but no fail history was supplied",
            );
          }
          errors.push({
            amendmentIndex: originalIndex,
            message: "Partially failed to apply amendment",
            amendment,
            actionString,
          });
          onAmendmentApplyPartialFail(amendment, result.failures);
        }
      } catch (error) {
        console.error(error);
        console.log(`== Failed to process amendment ${originalIndex} ==`);
        const errMsg = error instanceof Error ? error.message : "Unknown error";
        errors.push({
          amendmentIndex: originalIndex,
          message: errMsg,
          amendment,
          actionString,
        });
        onAmendmentApplyFail(amendment, errMsg);
      }
    }

    console.log(
      `== Summary ==\nSuccessfully applied ${completedAmendments.length}/${sortedAmendments.length} amendments`,
    );
    if (errors.length > 0) {
      console.log(
        `Failure reasons: \n${errors
          .map(
            ({amendmentIndex, message, actionString}) =>
              `\tAmendment ${amendmentIndex + 1} ${
                actionString ? `(${actionString})` : ""
              }, ${message}`,
          )
          .join("\n")}`,
      );
    }

    return {errors, completedAmendments};
  });
}

function getInsertAnchorText(amendment: DocumentAmendment): string | undefined {
  return amendment.action_type === "insert_before"
    ? amendment.after_text
    : amendment.before_text;
}

async function findInsertAnchorParagraph(
  context: Word.RequestContext,
  amendment: DocumentAmendment,
  logger: (str: string) => void,
): Promise<Word.Paragraph | null> {
  const anchorText = getInsertAnchorText(amendment);

  if (!anchorText) {
    return null;
  }

  const anchorRange = await findTextWithContext(
    context,
    anchorText,
    logger,
    amendment.before_text,
    amendment.after_text,
  );

  if (!anchorRange) {
    return null;
  }

  return anchorRange.paragraphs.getFirst();
}

async function resolveSubclauseListLevels(
  context: Word.RequestContext,
  subAmendments: DocumentAmendment[],
  logger: (str: string) => void,
): Promise<void> {
  if (!subAmendments.some(sub => sub.listLevel !== undefined)) {
    return;
  }

  const anchorParagraph = await findInsertAnchorParagraph(
    context,
    subAmendments[0],
    logger,
  );

  if (!anchorParagraph) {
    return;
  }

  anchorParagraph.load("isListItem");
  await context.sync();

  if (!anchorParagraph.isListItem) {
    return;
  }

  anchorParagraph.listItem.load("level");
  await context.sync();

  const anchorLevel = anchorParagraph.listItem.level;
  logger(
    `Resolving subclause list levels relative to anchor level ${anchorLevel}`,
  );

  for (const sub of subAmendments) {
    if (sub.listLevel !== undefined) {
      const absoluteLevel = anchorLevel + sub.listLevel;
      if (absoluteLevel < 0) {
        // No shallower level to promote to (flat list). Detach to a plain
        // paragraph instead of clamping into the sub-list, which mis-numbers it.
        sub.detachFromList = true;
        sub.listLevel = undefined;
      } else {
        sub.listLevel = absoluteLevel;
      }
    }
  }
}

async function applyMultiAmendment(
  context: Word.RequestContext,
  amendment: DocumentAmendment,
  logger: (str: string) => void,
): Promise<ProcessAmendmentStatus> {
  const subAmendments = amendment.subAmendments;

  if (subAmendments === undefined || !subAmendments) {
    throw new Error(
      `Multi amendment ${amendment} had no defined sub amendments`,
    );
  }

  await resolveSubclauseListLevels(context, subAmendments, logger);

  const failures: SubAmendmentFailure[] = [];

  for (const subAmendment of subAmendments) {
    try {
      await applySingleAmendment(context, subAmendment, logger);
    } catch (err) {
      console.error(err);
      failures.push({amendment: subAmendment, reason: err.message});
      console.log(`failed to apply sub amendment ${subAmendment}`);
    }
  }

  if (failures.length > 0) {
    return {status: "partial", failures};
  }

  return {status: "success"};
}

const endsWithSpace = (str: string) => /\s$/.test(str);
const needsSpaceBefore = (str: string) => /^[\w(\[{"\u201C\u2018]/.test(str);

export function ensureInlineSpacing(
  addedText: string,
  contextText: string | undefined,
  insertLocation: Word.InsertLocation,
): string {
  if (!contextText || !addedText) {
    return addedText;
  }

  if (insertLocation === Word.InsertLocation.after) {
    if (!endsWithSpace(contextText) && needsSpaceBefore(addedText)) {
      return ` ${addedText}`;
    }
  } else if (insertLocation === Word.InsertLocation.before) {
    if (!endsWithSpace(addedText) && needsSpaceBefore(contextText)) {
      return `${addedText} `;
    }
  }

  return addedText;
}

async function applySingleAmendment(
  context: Word.RequestContext,
  amendment: DocumentAmendment,
  logger: (str: string) => void,
): Promise<void> {
  let range: Word.Range;
  switch (amendment.action_type) {
    case "replace":
      range = await applyReplaceAmendment(context, amendment, logger);
      break;
    case "delete":
    case "delete_clause":
      range = await applyDeleteAmendment(context, amendment, logger);
      break;
    case "insert_before":
    case "insert_after":
      range = await applyInsertAmendment(context, amendment, logger);
      break;
    case "insert_inline":
      range = await applyInsertInlineAmendment(context, amendment, logger);
      break;
    default:
      throw new Error(`Unknown action type: ${amendment.action_type}`);
  }

  if (amendment.comment) {
    await addAmendmentComment(context, range, amendment.comment);
  }
}

async function applyReplaceAmendment(
  context: Word.RequestContext,
  amendment: DocumentAmendment,
  logger,
): Promise<Word.Range> {
  if (amendment.deleted_text.trim() === "") {
    logger(
      "Replace with empty deleted_text — routing to insert_inline using before/after anchors",
    );
    return applyInsertInlineAmendment(context, amendment, logger);
  }

  const targetRange = await findTextWithContext(
    context,
    amendment.deleted_text,
    logger,
    amendment.before_text,
    amendment.after_text,
  );

  if (!targetRange) {
    throw new Error(
      `Could not find text to replace: "${amendment.deleted_text}"`,
    );
  }
  logger("Attempting to replace text...");

  targetRange.load("text");
  await context.sync();

  const originalText = targetRange.text;
  const preservedAddedText = preserveFormatting(
    originalText,
    amendment.added_text,
  );

  insertPlainText(targetRange, preservedAddedText, Word.InsertLocation.replace);
  await context.sync();

  return targetRange;
}

// Whole-deletes covered paragraphs, keeps marks that end a section
async function deleteCoveredParagraphs(
  context: Word.RequestContext,
  paragraphs: Word.Paragraph[],
  targetRange: Word.Range,
  sections: Word.Section[],
): Promise<void> {
  const coveredRelations: string[] = [
    Word.LocationRelation.equal,
    Word.LocationRelation.inside,
    Word.LocationRelation.insideStart,
    Word.LocationRelation.insideEnd,
  ];
  const sectionEnds = sections.map(section =>
    section.body.paragraphs.getLast().getRange(),
  );
  const checks = paragraphs.map(para => {
    const range = para.getRange();
    return {
      para,
      coverage: range.compareLocationWith(targetRange),
      sectionEnds: sectionEnds.map(end => range.compareLocationWith(end)),
    };
  });
  await context.sync();

  const partial: Word.Range[] = [];
  for (const check of checks) {
    const isCovered = coveredRelations.includes(check.coverage.value);
    const isSectionEnd = check.sectionEnds.some(
      relation => relation.value === Word.LocationRelation.equal,
    );
    if (isCovered && !isSectionEnd) {
      check.para.delete();
    } else {
      // Content only: the paragraph mark stays
      const overlap = check.para
        .getRange(Word.RangeLocation.content)
        .intersectWithOrNullObject(targetRange);
      overlap.load("isNullObject");
      partial.push(overlap);
    }
  }
  await context.sync();

  for (const overlap of partial) {
    if (!overlap.isNullObject) {
      overlap.delete();
    }
  }
  await context.sync();
}

async function deleteSeparatedHeadingIfPresent(
  context: Word.RequestContext,
  paragraph: Word.Paragraph,
  deletedText: string,
  logger: (str: string) => void,
): Promise<boolean> {
  const prevParagraph = paragraph.getPrevious();
  prevParagraph.load(["isNullObject", "text"]);
  paragraph.load("text");
  await context.sync();

  if (prevParagraph.isNullObject) {
    return false;
  }

  const deletedTextNormalized = normalizeText(deletedText);
  const paragraphNormalized = normalizeText(paragraph.text);
  const prevTextNormalized = normalizeText(prevParagraph.text);

  if (
    prevTextNormalized.length > 0 &&
    !deletedTextNormalized.startsWith(paragraphNormalized) &&
    deletedTextNormalized.startsWith(prevTextNormalized)
  ) {
    logger(
      `Detected heading in previous paragraph: "${prevParagraph.text.trim()}", including in deletion`,
    );
    prevParagraph.delete();
    await context.sync();
    return true;
  }

  return false;
}

async function deleteParagraphOrRange(
  context: Word.RequestContext,
  targetRange: Word.Range,
  paragraph: Word.Paragraph,
  deletedText: string,
  logger: (str: string) => void,
): Promise<void> {
  paragraph.load("text");
  await context.sync();

  const paragraphTextNormalized = normalizeText(paragraph.text);
  const deletedTextNormalized = normalizeText(deletedText);
  const targetRangeTextNormalized = normalizeText(targetRange.text);

  // check deleted_text matches the entire paragraph
  if (
    paragraphTextNormalized === deletedTextNormalized ||
    paragraphTextNormalized === targetRangeTextNormalized
  ) {
    logger("Deleted text matches entire paragraph - deleting paragraph");
    await deleteSeparatedHeadingIfPresent(
      context,
      paragraph,
      deletedText,
      logger,
    );
    paragraph.delete();
    await context.sync();
  } else {
    // Deleting partial text within paragraph
    logger(
      "Deleted text is partial - deleting range only (not entire paragraph)",
    );
    targetRange.delete();
    await context.sync();
  }
}

async function applyDeleteAmendment(
  context: Word.RequestContext,
  amendment: DocumentAmendment,
  logger: (str: string) => void,
): Promise<Word.Range> {
  const targetRange = await findTextWithContext(
    context,
    amendment.deleted_text,
    logger,
    amendment.before_text,
    amendment.after_text,
  );

  if (!targetRange) {
    throw new Error(
      `Could not find text to delete: "${amendment.deleted_text}"`,
    );
  }
  logger("Attempting to delete text...");

  targetRange.load("text");
  await context.sync();

  const paragraphs = targetRange.paragraphs;
  const sections = context.document.sections;
  paragraphs.load("items");
  sections.load("items");
  await context.sync();

  // If multiple paragraphs, delete all of them
  if (paragraphs.items.length > 1) {
    logger(
      `Deleting ${paragraphs.items.length} paragraphs (multi-paragraph deletion)`,
    );

    const firstParagraph = paragraphs.items[0];
    if (firstParagraph) {
      await deleteSeparatedHeadingIfPresent(
        context,
        firstParagraph,
        amendment.deleted_text,
        logger,
      );
    }

    await deleteCoveredParagraphs(
      context,
      paragraphs.items,
      targetRange,
      sections.items,
    );

    return targetRange;
  }

  await deleteParagraphOrRange(
    context,
    targetRange,
    paragraphs.items[0],
    amendment.deleted_text,
    logger,
  );

  return targetRange;
}

export async function isRenumberTargetAutoNumbered(
  context: Word.RequestContext,
  amendment: DocumentAmendment,
  logger: (str: string) => void = () => undefined,
): Promise<boolean> {
  const anchorText = (
    amendment.after_text ||
    amendment.before_text ||
    ""
  ).trim();
  if (!anchorText) {
    return false;
  }

  const anchorRange = await findTextWithContext(
    context,
    anchorText,
    logger,
    amendment.before_text,
  );
  if (!anchorRange) {
    return false;
  }

  const paragraph = anchorRange.paragraphs.getFirst();
  const listItem = paragraph.listItemOrNullObject;
  const list = paragraph.listOrNullObject;
  listItem.load("isNullObject,level");
  list.load("levelTypes");
  await context.sync();

  if (listItem.isNullObject) {
    return false;
  }

  return list.levelTypes[listItem.level] === Word.ListLevelType.number;
}

async function detectAutoNumbering(
  context: Word.RequestContext,
  anchorParagraph: Word.Paragraph,
  actionType: string,
): Promise<{isAutoNumbered: boolean; adjacentText: string}> {
  anchorParagraph.load("isListItem,text");
  await context.sync();

  if (anchorParagraph.isListItem) {
    return {isAutoNumbered: true, adjacentText: anchorParagraph.text};
  }

  // Fallback: check the sibling on the insertion side
  const sibling =
    actionType === "insert_after"
      ? anchorParagraph.getNextOrNullObject()
      : anchorParagraph.getPreviousOrNullObject();
  sibling.load("isNullObject,isListItem,text");
  await context.sync();

  if (!sibling.isNullObject && sibling.isListItem) {
    return {isAutoNumbered: true, adjacentText: sibling.text};
  }

  return {isAutoNumbered: false, adjacentText: anchorParagraph.text};
}

function adjustTextForNumbering(
  text: string,
  isAutoNumbered: boolean,
  adjacentText: string,
  amendment: DocumentAmendment,
  logger: (str: string) => void,
): string {
  if (isAutoNumbered && !amendment.detachFromList) {
    // Strip any clause number prefix the LLM may have included
    const stripped = stripClauseNumberPrefix(text);
    if (stripped !== text) {
      logger(
        `Auto-numbering detected — stripped number prefix: "${text.substring(
          0,
          30,
        )}" -> "${stripped.substring(0, 30)}"`,
      );
    }
    return stripped;
  }

  if (
    !isAutoNumbered &&
    amendment.target_clause_reference &&
    !amendment.detachFromList
  ) {
    // Manual numbering — prepend number if not already present
    if (!hasClauseNumberPrefix(text)) {
      const component = extractLastReferenceComponent(
        amendment.target_clause_reference,
      );
      if (component) {
        const prefix = formatNumberPrefix(component, adjacentText);
        logger(`Manual numbering detected — prepending "${prefix}" to text`);
        return prefix + text;
      }
    }
  }

  return text;
}

async function applyInsertAmendment(
  context: Word.RequestContext,
  amendment: DocumentAmendment,
  logger,
): Promise<Word.Range> {
  if (!amendment.added_text) {
    throw new Error(
      `Invalid insertion amendment: missing added_text for ${amendment.action_type}`,
    );
  }

  let anchorParagraph: Word.Paragraph | null = await findInsertAnchorParagraph(
    context,
    amendment,
    logger,
  );

  if (!anchorParagraph) {
    const anchorText = getInsertAnchorText(amendment);
    if (!anchorText) {
      logger("No anchor text found - inserting at end of document");
      anchorParagraph = context.document.body.paragraphs.getLastOrNullObject();
      await context.sync();
    } else {
      throw new Error(
        `Could not find anchor text for insertion: "${anchorText}"`,
      );
    }
  }

  logger("Attempt to insert text...");

  const {isAutoNumbered, adjacentText} = await detectAutoNumbering(
    context,
    anchorParagraph,
    amendment.action_type,
  );

  const textToInsert = adjustTextForNumbering(
    amendment.added_text.trimEnd(),
    isAutoNumbered,
    adjacentText,
    amendment,
    logger,
  );

  if (amendment.detachFromList || amendment.listLevel !== undefined) {
    const newParagraph = await insertSubclauseParagraph(
      context,
      anchorParagraph,
      textToInsert,
      amendment.action_type,
      amendment.listLevel,
      amendment.matchParentClauseIndent,
    );
    return newParagraph.getRange();
  } else {
    let newParagraph: Word.Paragraph;
    if (amendment.action_type === "insert_after") {
      newParagraph = anchorParagraph.insertParagraph(
        "",
        Word.InsertLocation.after,
      );
    } else {
      newParagraph = anchorParagraph.insertParagraph(
        "",
        Word.InsertLocation.before,
      );
    }
    await context.sync();

    insertPlainText(newParagraph, textToInsert, Word.InsertLocation.replace);
    await context.sync();
  }

  return anchorParagraph.getRange();
}

async function applyInsertInlineAmendment(
  context: Word.RequestContext,
  amendment: DocumentAmendment,
  logger: (str: string) => void,
): Promise<Word.Range> {
  if (!amendment.before_text && !amendment.after_text) {
    throw new Error(
      "Invalid insert_inline amendment: requires at least before_text or after_text",
    );
  }

  if (!amendment.added_text) {
    throw new Error(
      "Invalid insert_inline amendment: added_text cannot be empty",
    );
  }

  let insertionPoint: Word.Range | null = null;
  let insertLocation: Word.InsertLocation = Word.InsertLocation.after;

  if (amendment.before_text && amendment.after_text) {
    logger(
      "Insert inline: Finding before_text with after_text for disambiguation...",
    );
    const beforeRange = await findBeforeAnchorWithContext(
      context,
      amendment.before_text,
      amendment.after_text,
      logger,
    );

    if (beforeRange) {
      insertionPoint = beforeRange;
      insertLocation = Word.InsertLocation.after;
      logger("Insert inline: Found before_text anchor, will insert after it");
    }
  } else if (amendment.before_text) {
    logger("Insert inline: Finding before_text to insert after...");
    const beforeRange = await findTextWithContext(
      context,
      amendment.before_text,
      logger,
      undefined,
      amendment.after_text,
    );
    if (beforeRange) {
      insertionPoint = beforeRange;
      insertLocation = Word.InsertLocation.after;
    }
  } else if (amendment.after_text) {
    logger("Insert inline: Finding after_text to insert before...");
    const afterRange = await findTextWithContext(
      context,
      amendment.after_text,
      logger,
      amendment.before_text,
      undefined,
    );
    if (afterRange) {
      insertionPoint = afterRange;
      insertLocation = Word.InsertLocation.before;
    }
  }

  if (!insertionPoint) {
    throw new Error(
      `Could not find insertion point for insert_inline using before_text: "${amendment.before_text}" and/or after_text: "${amendment.after_text}"`,
    );
  }

  logger("Insert inline: Inserting text...");

  const contextText =
    insertLocation === Word.InsertLocation.after
      ? amendment.before_text
      : amendment.after_text;

  const textToInsert = ensureInlineSpacing(
    amendment.added_text.trimEnd(),
    contextText,
    insertLocation,
  );

  insertPlainText(insertionPoint, textToInsert, insertLocation);
  await context.sync();

  return insertionPoint;
}

async function addAmendmentComment(
  context: Word.RequestContext,
  range: Word.Range,
  text: string,
) {
  try {
    range.select();
    await context.sync();
    const selection = context.document.getSelection();
    selection.insertComment(text);
    await context.sync();
  } catch (error) {
    // Inserting the comment should never fail the amendment — the edit itself has
    // already applied. On Word for web, insertComment on the site of a
    // tracked deletion is rejected with "NotAllowed" (https://github.com/OfficeDev/office-js/issues/6746)
    // the comment is best-effort here while still working on desktop.
    console.error("addAmendmentComment failed:", error);
  }
}

// Ignores line-wrap hyphens
function normalizeLoose(text: string): string {
  return normalizeText(text).replace(/-/g, "");
}

async function searchChunkAcrossParagraph(
  context: Word.RequestContext,
  chunk: string,
  fromEnd: boolean,
  logger: (str: string) => void,
): Promise<Word.Range[]> {
  const expanded = await searchWithProgressiveExpansion(
    context,
    chunk,
    fromEnd,
  );
  if (!expanded) {
    return [];
  }
  logger(
    `Start+end strategy: ${fromEnd ? "End" : "Start"} chunk found with ${
      expanded.wordsUsed
    } words (${expanded.ranges.length} match(es))`,
  );
  return expanded.ranges;
}

async function findLongTextByChunks(
  context: Word.RequestContext,
  targetText: string,
  logger: (str: string) => void,
  beforeText?: string,
  afterText?: string,
): Promise<Word.Range | null> {
  const CHUNK_SIZE = 100;

  let startChunk = targetText.substring(0, CHUNK_SIZE);
  const lastSpace = startChunk.lastIndexOf(" ");
  if (lastSpace > 50) {
    startChunk = startChunk.substring(0, lastSpace).trim();
  }

  let endChunk = targetText.substring(targetText.length - CHUNK_SIZE);
  const firstSpace = endChunk.indexOf(" ");
  if (firstSpace > 0 && firstSpace < 50) {
    endChunk = endChunk.substring(firstSpace + 1).trim();
  }

  logger(
    `Start+end strategy: Searching for start chunk (${startChunk.length} chars) and end chunk (${endChunk.length} chars)`,
  );

  let startResults = await searchInDocument(context, startChunk);
  let endResults = await searchInDocument(context, endChunk);

  logger(
    `Start+end strategy: Found ${startResults.length} start matches, ${endResults.length} end matches`,
  );

  // Chunk may cross a paragraph mark
  if (startResults.length === 0) {
    startResults = await searchChunkAcrossParagraph(
      context,
      startChunk,
      false,
      logger,
    );
  }
  if (endResults.length === 0) {
    endResults = await searchChunkAcrossParagraph(
      context,
      endChunk,
      true,
      logger,
    );
  }

  if (startResults.length === 0 || endResults.length === 0) {
    return null;
  }

  const hasUniqueMatches = startResults.length === 1 && endResults.length === 1;
  const normalizedTarget = normalizeLoose(targetText);
  // Must start at the target and not run on past it
  const maxLength = normalizedTarget.length * 1.1 + 20;
  const combined = await findChunkPair(
    context,
    startResults,
    endResults,
    joined => joined.startsWith(normalizedTarget),
    maxLength,
    async candidate => {
      if (hasUniqueMatches) {
        logger(
          "Start+end strategy: Unique matches found, skipping context validation",
        );
        return true;
      }
      if (!beforeText && !afterText) {
        return true;
      }
      const isValid = await validateSingleResultWithContext(
        context,
        candidate,
        beforeText,
        afterText,
      );
      if (!isValid) {
        logger("Start+end strategy: Context validation failed");
      }
      return isValid;
    },
  );

  if (combined) {
    logger("Start+end strategy: Successfully found match");
    return combined;
  }
  logger("Start+end strategy: No start+end pair matched the target");
  return null;
}

interface PairTextMatch {
  matched: boolean;
  minLength: number;
}

// Redlines from earlier amendments sit in .text; reviewed text only on a miss
async function matchPairText(
  context: Word.RequestContext,
  pair: Word.Range,
  accept: (joinedText: string) => boolean,
  maxLength: number,
): Promise<PairTextMatch> {
  const fits = (text: string) => text.length <= maxLength && accept(text);
  const plain = normalizeLoose(pair.text);
  if (fits(plain)) {
    return {matched: true, minLength: plain.length};
  }
  let minLength = plain.length;
  if (supportsReviewedText()) {
    const reviewed = [
      pair.getReviewedText(Word.ChangeTrackingVersion.current),
      pair.getReviewedText(Word.ChangeTrackingVersion.original),
    ];
    await context.sync();
    for (const result of reviewed) {
      const text = normalizeLoose(result.value);
      if (fits(text)) {
        return {matched: true, minLength: text.length};
      }
      minLength = Math.min(minLength, text.length);
    }
  }
  return {matched: false, minLength};
}

// Joins start hits to end hits one pair at a time; returns the first join
// no longer than maxLength whose text passes accept and whose range passes
// isValid
async function findChunkPair(
  context: Word.RequestContext,
  starts: Word.Range[],
  ends: Word.Range[],
  accept: (joinedText: string) => boolean,
  maxLength: number,
  isValid: (pair: Word.Range) => Promise<boolean>,
): Promise<Word.Range | null> {
  // Start hit sits after the end hit: the join can never begin with the target
  const reversed: string[] = [
    Word.LocationRelation.after,
    Word.LocationRelation.adjacentAfter,
    Word.LocationRelation.overlapsAfter,
  ];
  const orders = starts.map(start =>
    ends.map(end => start.compareLocationWith(end)),
  );
  await context.sync();

  for (let s = 0; s < starts.length; s++) {
    let tooLong = false;
    for (let e = 0; e < ends.length && !tooLong; e++) {
      if (!reversed.includes(orders[s][e].value)) {
        const pair = starts[s].expandToOrNullObject(ends[e]);
        pair.load(["isNullObject", "text"]);
        await context.sync();
        if (!pair.isNullObject) {
          const match = await matchPairText(context, pair, accept, maxLength);
          if (match.matched && (await isValid(pair))) {
            return pair;
          }
          // Later end hits only make the join longer
          tooLong = !match.matched && match.minLength > maxLength;
        }
      }
    }
  }
  return null;
}

async function findTextWithContext(
  context: Word.RequestContext,
  targetText: string,
  logger: (str: string) => void,
  beforeText?: string,
  afterText?: string,
): Promise<Word.Range | null> {
  if (targetText.length > MAX_SEARCH_LENGTH) {
    logger(
      `Finding clause: Target text too long (${targetText.length} chars), using start+end strategy`,
    );
    const longTextResult = await findLongTextByChunks(
      context,
      targetText,
      logger,
      beforeText,
      afterText,
    );
    if (longTextResult) {
      return longTextResult;
    }
    logger("Finding clause: Start+end strategy failed");
    return null;
  }

  const directResults = await searchInDocument(context, targetText);

  if (directResults.length === 1) {
    logger(
      "Finding clause: Target text appears exactly once - using direct match",
    );
    return directResults[0];
  }

  logger(
    `Finding clause: Target text appears ${directResults.length} times, need context validation`,
  );

  if (directResults.length > 1 && beforeText && afterText) {
    // Target is itself an anchor: earlier amendments may sit between the anchors
    const anchorSide = getAnchorSide(targetText, beforeText, afterText);
    if (anchorSide) {
      const anchorRange = await findClosestAnchor(
        context,
        beforeText,
        afterText,
        anchorSide,
        logger,
      );
      if (anchorRange) {
        logger(
          `Finding clause: Target is the ${anchorSide} anchor - using closest anchor pair`,
        );
        return anchorRange;
      }
    }

    logger(
      "Finding clause: Multiple matches with both anchors present, trying anchor-based range...",
    );
    const anchorResult = await findRangeBetweenAnchors(
      context,
      beforeText,
      afterText,
      logger,
    );
    if (anchorResult) {
      const targetInRange = anchorResult.search(targetText, {
        matchCase: false,
        matchWholeWord: false,
      });
      targetInRange.load("items");
      await context.sync();

      if (targetInRange.items.length > 0) {
        logger(
          `Finding clause: Anchor-based range success - found target within range (${targetInRange.items.length} match(es))`,
        );
        return targetInRange.items[0];
      }
      logger(
        "Finding clause: Anchor-based range found but target text not within it",
      );
    }
    logger(
      "Finding clause: Anchor-based range failed, falling back to context validation",
    );
  }

  if (directResults.length > 0) {
    logger("Finding clause: Validating direct results with context...");
    const validResult = await validateWithContext(
      context,
      directResults,
      beforeText,
      afterText,
      false,
      undefined,
    );
    if (validResult) {
      logger("Finding clause: Direct search with context validation success");
      return validResult;
    }
  }

  logger("Finding clause: Searching with target text directly failed");
  logger("Finding clause: Searching with normalised text ...");

  const normalizedTarget = normalizeText(targetText);
  const normalizedResults = await searchInDocument(context, normalizedTarget);
  const validNormalizedResult = await validateWithContext(
    context,
    normalizedResults,
    beforeText,
    afterText,
    false,
    undefined,
  );
  if (validNormalizedResult) {
    logger("Finding clause: Searching with normalised text success");
    return validNormalizedResult;
  }

  logger("Finding clause: Searching with normalised text failed");

  logger("Finding clause: Searching with context ...");

  if (beforeText || afterText) {
    const contextResult = await findUsingContext(
      context,
      targetText,
      beforeText,
      afterText,
    );
    if (contextResult) {
      logger("Finding clause: Searching with context success");
      return contextResult;
    }
  }
  logger("Finding clause: Searching with context failed");
  logger("Finding clause: Searching with meaningful phrases ...");

  const meaningfulPhrases = extractMeaningfulPhrases(targetText);

  for (const phrase of meaningfulPhrases) {
    const results = await searchInDocument(context, phrase);
    const validPhraseResult = await validateWithContext(
      context,
      results,
      beforeText,
      afterText,
      true,
      targetText,
    );
    if (validPhraseResult) {
      logger("Finding clause: Searching with meaningful phrases success");
      return validPhraseResult;
    }
  }

  logger("Finding clause: Searching with meaningful phrases failed");
  return null;
}

async function validateWithContext(
  context: Word.RequestContext,
  searchResults: Word.Range[],
  beforeText?: string,
  afterText?: string,
  allowBypassValidation = false,
  targetText?: string,
): Promise<Word.Range | null> {
  if (searchResults.length === 0) {
    return null;
  }

  if (searchResults.length === 1) {
    if (!beforeText && !afterText) {
      return searchResults[0];
    }

    const expandedRange = await validateAndExpandSingleResult(
      context,
      searchResults[0],
      beforeText,
      afterText,
      targetText,
    );

    if (expandedRange) {
      console.log(
        "validateWithContext: Single result, context validation passed",
      );
      return expandedRange;
    }

    // Context validation failed
    if (allowBypassValidation) {
      console.log(
        "validateWithContext: Single result, context validation failed (returning anyway - bypass allowed)",
      );
      try {
        const paragraph = searchResults[0].paragraphs.getFirst().getRange();
        paragraph.load("text");
        await context.sync();
        if (targetText) {
          return await expandRangeToTarget(
            context,
            paragraph,
            [],
            targetText,
            TARGET_COVERAGE_THRESHOLD,
            MAX_EXPANSION_RATIO,
          );
        }
        return paragraph;
      } catch {
        return searchResults[0];
      }
    } else {
      console.log(
        "validateWithContext: Single result, context validation failed (rejecting - bypass not allowed)",
      );
      return null;
    }
  }

  for (const result of searchResults) {
    const expandedRange = await validateAndExpandSingleResult(
      context,
      result,
      beforeText,
      afterText,
      targetText,
    );
    if (expandedRange) {
      return expandedRange;
    }
  }

  console.log(
    `validateWithContext: Multiple results (${searchResults.length}), none passed context validation`,
  );
  return null;
}

async function validateAndExpandSingleResult(
  context: Word.RequestContext,
  result: Word.Range,
  beforeText?: string,
  afterText?: string,
  targetText?: string,
): Promise<Word.Range | null> {
  const CONTEXT_PARAGRAPH_LOOKAHEAD = 5;

  try {
    const paragraph = result.paragraphs.getFirst().getRange();
    const prevParagraph = paragraph.paragraphs.getFirst().getPrevious();

    paragraph.load("text");
    prevParagraph.load(["isNullObject", "text"]);

    const nextParagraphs = await loadNextParagraphs(
      paragraph,
      CONTEXT_PARAGRAPH_LOOKAHEAD,
    );

    await context.sync();

    const anchorsValid = await validateContextAnchorsWithRedlines(
      context,
      paragraph,
      prevParagraph,
      nextParagraphs,
      beforeText,
      afterText,
    );
    if (!anchorsValid) {
      return null;
    }

    if (targetText) {
      return expandRangeToTarget(
        context,
        paragraph,
        nextParagraphs,
        targetText,
        TARGET_COVERAGE_THRESHOLD,
        MAX_EXPANSION_RATIO,
      );
    }

    return result;
  } catch (error) {
    console.error("validateAndExpandSingleResult: Word call failed", error);
    return null;
  }
}

async function loadNextParagraphs(
  paragraph: Word.Range,
  count: number,
): Promise<Word.Paragraph[]> {
  const paragraphs: Word.Paragraph[] = [];
  let currentPara = paragraph.paragraphs.getLast();

  for (let i = 0; i < count; i++) {
    const next = currentPara.getNext();
    next.load(["isNullObject", "text"]);
    paragraphs.push(next);
    currentPara = next;
  }

  return paragraphs;
}

function buildContextText(
  paragraphText: string,
  prevParagraph: Word.Paragraph,
  nextParagraphs: Word.Paragraph[],
): string {
  let contextText = paragraphText;

  if (!prevParagraph.isNullObject) {
    contextText = `${prevParagraph.text} ${contextText}`;
  }

  for (const next of nextParagraphs) {
    if (next.isNullObject) {
      break;
    }
    contextText = `${contextText} ${next.text}`;
  }

  return contextText;
}

function supportsReviewedText(): boolean {
  try {
    return Office.context.requirements.isSetSupported("WordApi", "1.4");
  } catch {
    return false;
  }
}

// Retries failed anchor checks with redlines accepted, then rejected
async function validateContextAnchorsWithRedlines(
  context: Word.RequestContext,
  paragraph: Word.Range,
  prevParagraph: Word.Paragraph,
  nextParagraphs: Word.Paragraph[],
  beforeText?: string,
  afterText?: string,
): Promise<boolean> {
  const displayed = buildContextText(
    paragraph.text,
    prevParagraph,
    nextParagraphs,
  );
  if (validateContextAnchors(displayed, beforeText, afterText)) {
    return true;
  }
  if (!supportsReviewedText()) {
    return false;
  }
  try {
    const versions = [
      Word.ChangeTrackingVersion.current,
      Word.ChangeTrackingVersion.original,
    ];
    const loads = versions.map(version => ({
      para: paragraph.getReviewedText(version),
      prev: prevParagraph.isNullObject
        ? null
        : prevParagraph.getRange().getReviewedText(version),
      next: nextParagraphs.map(next =>
        next.isNullObject ? null : next.getRange().getReviewedText(version),
      ),
    }));
    await context.sync();
    for (const load of loads) {
      let text = load.para.value;
      if (load.prev) {
        text = `${load.prev.value} ${text}`;
      }
      for (const next of load.next) {
        if (!next) {
          break;
        }
        text = `${text} ${next.value}`;
      }
      if (validateContextAnchors(text, beforeText, afterText)) {
        return true;
      }
    }
  } catch (error) {
    console.error(
      "validateContextAnchorsWithRedlines: getReviewedText failed",
      error,
    );
  }
  return false;
}

function validateContextAnchors(
  contextText: string,
  beforeText?: string,
  afterText?: string,
): boolean {
  const normalizedContext = normalizeText(contextText);

  if (beforeText) {
    const normalizedBefore = normalizeText(beforeText);
    if (!isContextTextPresent(normalizedContext, normalizedBefore)) {
      return false;
    }
  }

  if (afterText) {
    const normalizedAfter = normalizeText(afterText);
    if (!isContextTextPresent(normalizedContext, normalizedAfter)) {
      return false;
    }
  }

  return true;
}

async function expandRangeToTarget(
  context: Word.RequestContext,
  paragraph: Word.Range,
  nextParagraphs: Word.Paragraph[],
  targetText: string,
  coverageThreshold: number,
  maxExpansionRatio: number,
): Promise<Word.Range | null> {
  const normalizedTarget = normalizeText(targetText);
  const paragraphText = normalizeText(paragraph.text);

  if (paragraphText.length > normalizedTarget.length * maxExpansionRatio) {
    // Paragraph is much longer than the target: narrow to the target
    const within = await findTargetWithinRange(context, paragraph, targetText);
    if (within) {
      return within;
    }
    const following = nextParagraphs.filter(next => !next.isNullObject);
    if (following.length === 0) {
      return null;
    }
    const wide = paragraph.expandTo(following[following.length - 1].getRange());
    return findTargetWithinRange(context, wide, targetText);
  }

  if (paragraphText.length >= normalizedTarget.length * coverageThreshold) {
    return paragraph;
  }

  let expandedRange = paragraph;
  let expandedText = paragraphText;

  for (const next of nextParagraphs) {
    if (next.isNullObject) {
      break;
    }

    const nextText = normalizeText(next.text);
    const testExpandedText = `${expandedText} ${nextText}`;

    if (testExpandedText.length > normalizedTarget.length * maxExpansionRatio) {
      break;
    }

    const nextRange = next.getRange();
    nextRange.load("text");
    await context.sync();

    expandedRange = expandedRange.expandTo(nextRange);
    expandedText = testExpandedText;

    // Stop if we've achieved sufficient coverage
    if (expandedText.length >= normalizedTarget.length * coverageThreshold) {
      break;
    }
  }

  return expandedRange;
}

async function findTargetWithinRange(
  context: Word.RequestContext,
  range: Word.Range,
  targetText: string,
): Promise<Word.Range | null> {
  const words = normalizeText(targetText)
    .split(" ")
    .filter(word => word.length > 0);
  if (words.length === 0) {
    return null;
  }
  const chunkWords = Math.min(4, words.length);
  const starts = await searchRange(
    context,
    range,
    words.slice(0, chunkWords).join(" "),
  );
  const ends = await searchRange(
    context,
    range,
    words.slice(-chunkWords).join(" "),
  );

  const looseTarget = normalizeLoose(targetText);
  const match = await findChunkPair(
    context,
    starts,
    ends,
    joined => joined === looseTarget,
    looseTarget.length,
    async () => true,
  );
  if (match) {
    console.log("findTargetWithinRange: Narrowed paragraph match to target");
    return match;
  }
  console.log("findTargetWithinRange: Target not found within paragraph");
  return null;
}

async function validateSingleResultWithContext(
  context: Word.RequestContext,
  result: Word.Range,
  beforeText?: string,
  afterText?: string,
): Promise<boolean> {
  try {
    const paragraph = result.paragraphs.getFirst().getRange();

    const nextParagraphs: Word.Paragraph[] = [];
    const prevParagraph = paragraph.paragraphs.getFirst().getPrevious();

    paragraph.load("text");
    prevParagraph.load(["isNullObject", "text"]);

    let currentPara = paragraph.paragraphs.getLast();
    for (let i = 0; i < 3; i++) {
      const next = currentPara.getNext();
      next.load(["isNullObject", "text"]);
      nextParagraphs.push(next);
      currentPara = next;
    }

    // Single sync for all loads
    await context.sync();

    return await validateContextAnchorsWithRedlines(
      context,
      paragraph,
      prevParagraph,
      nextParagraphs,
      beforeText,
      afterText,
    );
  } catch (error) {
    return beforeText === undefined && afterText === undefined;
  }
}

async function findUsingContext(
  context: Word.RequestContext,
  targetText: string,
  beforeText?: string,
  afterText?: string,
): Promise<Word.Range | null> {
  const searchTermsWithConfig: Array<{
    term: string;
    fromEnd: boolean;
  }> = [];

  if (beforeText) {
    searchTermsWithConfig.push({term: beforeText, fromEnd: true});
  }
  if (afterText) {
    searchTermsWithConfig.push({term: afterText, fromEnd: false});
  }

  for (const {term: searchTerm, fromEnd} of searchTermsWithConfig) {
    let results = await searchInDocument(context, searchTerm);

    if (results.length === 0) {
      const expanded = await searchWithProgressiveExpansion(
        context,
        searchTerm,
        fromEnd,
      );
      if (expanded) {
        results = expanded.ranges;
      }
    }

    for (const result of results) {
      // Expand the context around this result
      const expandedRange = await expandFromAnchor(context, result, targetText);
      if (expandedRange) {
        expandedRange.load("text");
        await context.sync();

        const expandedText = expandedRange.text;
        const normalizedExpanded = normalizeText(expandedText);
        const normalizedTarget = normalizeText(targetText);

        // Check if this expanded range contains target and context
        if (normalizedExpanded.includes(normalizedTarget)) {
          const isValid = await validateSingleResultWithContext(
            context,
            result,
            beforeText,
            afterText,
          );
          if (isValid) {
            const exactRange = expandedRange.search(targetText, {
              matchCase: false,
              matchWholeWord: false,
            });
            exactRange.load("items");
            await context.sync();

            if (exactRange.items.length > 0) {
              return exactRange.items[0];
            }
          }
        }
      }
    }
  }

  return null;
}

function getAnchorSide(
  targetText: string,
  beforeText: string,
  afterText: string,
): "before" | "after" | null {
  const target = normalizeText(targetText);
  if (target === normalizeText(afterText)) {
    return "after";
  }
  if (target === normalizeText(beforeText)) {
    return "before";
  }
  return null;
}

async function findClosestAnchor(
  context: Word.RequestContext,
  beforeText: string,
  afterText: string,
  side: "before" | "after",
  logger: (str: string) => void,
): Promise<Word.Range | null> {
  const beforeResults = await searchInDocument(context, beforeText);
  const afterResults = await searchInDocument(context, afterText);
  if (beforeResults.length === 0 || afterResults.length === 0) {
    return null;
  }
  const pair = await findClosestAnchorPair(
    context,
    beforeResults,
    afterResults,
    logger,
  );
  if (!pair) {
    return null;
  }
  return side === "after" ? pair.afterRange : pair.beforeRange;
}

async function findRangeBetweenAnchors(
  context: Word.RequestContext,
  beforeText: string,
  afterText: string,
  logger: (str: string) => void,
): Promise<Word.Range | null> {
  try {
    let beforeResults = await searchInDocument(context, beforeText);
    let afterResults = await searchInDocument(context, afterText);

    if (beforeResults.length === 0 || afterResults.length === 0) {
      logger(
        `findRangeBetweenAnchors: Missing anchors - before: ${beforeResults.length}, after: ${afterResults.length}`,
      );

      if (beforeResults.length === 0 && beforeText) {
        const normalizedBefore = normalizeText(beforeText);
        const beforeNormResults = await searchInDocument(
          context,
          normalizedBefore,
        );
        if (beforeNormResults.length > 0) {
          logger(
            "findRangeBetweenAnchors: Found before_text using normalized search",
          );
          beforeResults = beforeNormResults;
        }
      }

      if (afterResults.length === 0 && afterText) {
        const normalizedAfter = normalizeText(afterText);
        const afterNormResults = await searchInDocument(
          context,
          normalizedAfter,
        );
        if (afterNormResults.length > 0) {
          logger(
            "findRangeBetweenAnchors: Found after_text using normalized search",
          );
          afterResults = afterNormResults;
        }
      }

      if (beforeResults.length === 0 && beforeText) {
        logger(
          "findRangeBetweenAnchors: Trying progressive expansion for before_text (last N words)",
        );
        const expanded = await searchWithProgressiveExpansion(
          context,
          beforeText,
          true,
        );
        if (expanded) {
          logger(
            `findRangeBetweenAnchors: Progressive expansion found ${expanded.ranges.length} before_text candidate(s) with ${expanded.wordsUsed} words`,
          );
          beforeResults = expanded.ranges;
        }
      }

      if (afterResults.length === 0 && afterText) {
        logger(
          "findRangeBetweenAnchors: Trying progressive expansion for after_text (first N words)",
        );
        const expanded = await searchWithProgressiveExpansion(
          context,
          afterText,
          false,
        );
        if (expanded) {
          logger(
            `findRangeBetweenAnchors: Progressive expansion found ${expanded.ranges.length} after_text candidate(s) with ${expanded.wordsUsed} words`,
          );
          afterResults = expanded.ranges;
        }
      }

      if (beforeResults.length === 0 && afterResults.length === 1) {
        logger(
          "findRangeBetweenAnchors: No before_text but unique after_text (1 match) - using after_text to locate target",
        );
        return expandToAdjacentParagraph(context, afterResults[0], "previous");
      }

      if (afterResults.length === 0 && beforeResults.length === 1) {
        logger(
          "findRangeBetweenAnchors: No after_text but unique before_text (1 match) - using before_text to locate target",
        );
        return expandToAdjacentParagraph(context, beforeResults[0], "next");
      }

      if (beforeResults.length === 0 || afterResults.length === 0) {
        logger(
          `findRangeBetweenAnchors: Could not find anchors after all strategies - before: ${beforeResults.length}, after: ${afterResults.length}`,
        );
        return null;
      }
    }

    return tryPairAnchors(context, beforeResults, afterResults, logger);
  } catch (error) {
    logger(`Error in findRangeBetweenAnchors: ${error?.message}`);
    return null;
  }
}

async function tryPairAnchors(
  context: Word.RequestContext,
  beforeResults: Word.Range[],
  afterResults: Word.Range[],
  logger: (str: string) => void,
): Promise<Word.Range | null> {
  const result = await findClosestAnchorPair(
    context,
    beforeResults,
    afterResults,
    logger,
  );
  return result?.betweenRange ?? null;
}

async function findClosestAnchorPair(
  context: Word.RequestContext,
  beforeResults: Word.Range[],
  afterResults: Word.Range[],
  logger: (str: string) => void,
): Promise<AnchorPairResult | null> {
  const validPairs: AnchorPairResult[] = [];

  for (const beforeRange of beforeResults) {
    // One compare batch per before hit
    const orders: Array<ReturnType<Word.Range["compareLocationWith"]>> = [];
    for (const afterRange of afterResults) {
      orders.push(beforeRange.compareLocationWith(afterRange));
    }
    await context.sync();

    let found = false;
    // Hits come in document order: the first ordered after hit is the nearest
    for (let a = 0; a < afterResults.length && !found; a++) {
      const afterRange = afterResults[a];
      // Before anchor must precede after anchor
      const isOrdered = orders[a].value === Word.LocationRelation.before;
      if (isOrdered) {
        const betweenRange = getRangeBetween(beforeRange, afterRange);
        betweenRange.load(["isNullObject", "text"]);
        await context.sync();

        if (!betweenRange.isNullObject) {
          validPairs.push({
            beforeRange,
            afterRange,
            // Fresh proxy so the loaded text is not kept
            betweenRange: getRangeBetween(beforeRange, afterRange),
            distance: betweenRange.text?.length ?? 0,
          });
          found = true;
        }
      }
    }
  }

  if (validPairs.length === 0) {
    return null;
  }

  validPairs.sort((a, b) => a.distance - b.distance);
  logger(
    `tryPairAnchors: Found ${validPairs.length} valid pair(s), selecting closest (distance: ${validPairs[0].distance})`,
  );
  return validPairs[0];
}

function getRangeBetween(
  beforeRange: Word.Range,
  afterRange: Word.Range,
): Word.Range {
  const startPoint = beforeRange.getRange(Word.RangeLocation.after);
  const endPoint = afterRange.getRange(Word.RangeLocation.start);
  return startPoint.expandToOrNullObject(endPoint);
}

async function findBeforeAnchorWithContext(
  context: Word.RequestContext,
  beforeText: string,
  afterText: string,
  logger: (str: string) => void,
): Promise<Word.Range | null> {
  let beforeResults = await searchInDocument(context, beforeText);
  let afterResults = await searchInDocument(context, afterText);

  if (beforeResults.length === 0 && beforeText) {
    logger(
      "findBeforeAnchorWithContext: No direct matches, trying progressive expansion for before_text",
    );
    const expanded = await searchWithProgressiveExpansion(
      context,
      beforeText,
      true,
    );
    if (expanded) {
      logger(
        `findBeforeAnchorWithContext: Progressive expansion found ${expanded.ranges.length} before_text candidate(s) with ${expanded.wordsUsed} words`,
      );
      beforeResults = expanded.ranges;
    }
  }

  if (beforeResults.length === 0) {
    logger("findBeforeAnchorWithContext: No matches for before_text");
    return null;
  }

  if (beforeResults.length === 1) {
    logger("findBeforeAnchorWithContext: Single before_text match found");
    return beforeResults[0];
  }

  if (afterResults.length === 0 && afterText) {
    logger(
      "findBeforeAnchorWithContext: Trying progressive expansion for after_text",
    );
    const expanded = await searchWithProgressiveExpansion(
      context,
      afterText,
      false,
    );
    if (expanded) {
      logger(
        `findBeforeAnchorWithContext: Progressive expansion found ${expanded.ranges.length} after_text candidate(s) with ${expanded.wordsUsed} words`,
      );
      afterResults = expanded.ranges;
    }
  }

  if (afterResults.length === 0) {
    logger(
      "findBeforeAnchorWithContext: Multiple before_text matches but no after_text, using first match",
    );
    return beforeResults[0];
  }

  const result = await findClosestAnchorPair(
    context,
    beforeResults,
    afterResults,
    logger,
  );

  if (result) {
    logger(
      `findBeforeAnchorWithContext: Found closest pair with distance ${result.distance}`,
    );
    return result.beforeRange;
  }

  logger(
    "findBeforeAnchorWithContext: Could not pair anchors, using first before_text match",
  );
  return beforeResults[0];
}

export function isContextTextPresent(
  contextText: string,
  targetContext: string,
): boolean {
  if (contextText.includes(targetContext)) {
    return true;
  }

  // Word sequence matching - find longest consecutive word matches
  const targetWords = targetContext.split(/\s+/);

  const minMatchLength =
    targetWords.length <= 5
      ? targetWords.length
      : Math.max(5, Math.ceil(targetWords.length * 0.7));

  for (let seqLen = targetWords.length; seqLen >= minMatchLength; seqLen--) {
    for (let i = 0; i <= targetWords.length - seqLen; i++) {
      const sequence = targetWords.slice(i, i + seqLen).join(" ");
      if (contextText.includes(sequence)) {
        return true;
      }
    }
  }

  return false;
}

function extractMeaningfulPhrases(text: string): string[] {
  const phrases: string[] = [];
  const normalized = normalizeText(text);
  const words = normalized.split(/\s+/);
  const wordCount = words.length;

  // For long text skip phrase extraction
  if (wordCount > 150) {
    return [];
  }

  // Add the full text first
  phrases.push(normalized);

  // Handle clause/section numbering variations
  const clauseNumberPattern = /\b(\d+(?:\.\d+)*)\s*\.?\s+([a-z])/gi;
  const clauseVariations: string[] = [];
  let match: RegExpExecArray | null;
  while ((match = clauseNumberPattern.exec(normalized)) !== null) {
    const number = match[1];
    const nextChar = match[2];
    const startIndex = match.index;

    // Extract text around the clause number
    const beforeClause = normalized
      .substring(Math.max(0, startIndex - 20), startIndex)
      .trim();
    const afterClause = normalized
      .substring(match.index + match[0].length - 1, match.index + 50)
      .trim();

    // Generate variations
    clauseVariations.push(
      `${number} ${nextChar}${afterClause.substring(1, 15)}`,
    );
    clauseVariations.push(
      `${number}. ${nextChar}${afterClause.substring(1, 15)}`,
    );
    if (beforeClause) {
      clauseVariations.push(
        `${beforeClause} ${number} ${nextChar}${afterClause.substring(1, 10)}`,
      );
    }
  }

  phrases.push(...clauseVariations);

  // Extract phrases based on text length
  if (wordCount <= 20) {
    // Short text: extract overlapping sequences
    for (let length = Math.min(6, wordCount); length >= 3; length--) {
      for (let i = 0; i <= wordCount - length; i++) {
        const phrase = words.slice(i, i + length).join(" ");
        phrases.push(phrase);
      }
    }
  } else {
    // Extract longer phrases (6-10 words) from start, middle, and end
    const phraseLengths = [10, 8, 6].filter(len => len <= wordCount);

    for (const length of phraseLengths) {
      phrases.push(words.slice(0, length).join(" "));

      const midPoint = Math.floor((wordCount - length) / 2);
      phrases.push(words.slice(midPoint, midPoint + length).join(" "));

      phrases.push(words.slice(wordCount - length, wordCount).join(" "));

      // other strategic positions
      if (wordCount > 30) {
        const quarter = Math.floor((wordCount - length) / 4);
        phrases.push(words.slice(quarter, quarter + length).join(" "));
        const threeQuarter = Math.floor(((wordCount - length) * 3) / 4);
        phrases.push(
          words.slice(threeQuarter, threeQuarter + length).join(" "),
        );
      }
    }
  }
  const uniquePhrases = new Set(phrases);
  const result = Array.from(uniquePhrases);
  console.log(
    `extractMeaningfulPhrases: Extracted ${result.length} phrases from ${wordCount} words`,
  );
  return result;
}

async function expandFromAnchor(
  context: Word.RequestContext,
  anchorRange: Word.Range,
  targetText: string,
): Promise<Word.Range | null> {
  try {
    const normalizedTarget = normalizeText(targetText);

    const paragraph = anchorRange.paragraphs.getFirst().getRange();
    paragraph.load("text");
    await context.sync();

    const paragraphText = normalizeText(paragraph.text);

    if (paragraphText.includes(normalizedTarget)) {
      return paragraph;
    }

    const nextParagraph = anchorRange.paragraphs.getFirst().getNext();
    nextParagraph.load("isNullObject");
    await context.sync();

    if (!nextParagraph.isNullObject) {
      const expandedRange = paragraph.expandTo(nextParagraph.getRange());
      expandedRange.load("text");
      await context.sync();

      const expandedText = normalizeText(expandedRange.text);

      if (expandedText.includes(normalizedTarget)) {
        return expandedRange;
      }
    }

    return paragraph;
  } catch (error) {
    return anchorRange;
  }
}

async function findEnclosingClauseIndent(
  context: Word.RequestContext,
  anchorParagraph: Word.Paragraph,
): Promise<number | undefined> {
  anchorParagraph.load("isListItem");
  await context.sync();

  if (!anchorParagraph.isListItem) {
    return undefined;
  }

  const ancestor = anchorParagraph.listItem.getAncestorOrNullObject();
  ancestor.load("isNullObject,leftIndent");
  await context.sync();

  return ancestor.isNullObject ? undefined : ancestor.leftIndent;
}

async function insertSubclauseParagraph(
  context: Word.RequestContext,
  anchorParagraph: Word.Paragraph,
  text: string,
  actionType: string,
  listLevel?: number,
  matchParentClauseIndent = false,
): Promise<Word.Paragraph> {
  const safeListLevel =
    listLevel !== undefined ? Math.max(0, listLevel) : undefined;

  anchorParagraph.load("leftIndent,isListItem");
  await context.sync();

  const insertLocation =
    actionType === "insert_after"
      ? Word.InsertLocation.after
      : Word.InsertLocation.before;

  if (matchParentClauseIndent) {
    const parentIndent = await findEnclosingClauseIndent(
      context,
      anchorParagraph,
    );
    const newParagraph = anchorParagraph.insertParagraph("", insertLocation);

    if (anchorParagraph.isListItem) {
      newParagraph.detachFromList();
    }
    newParagraph.leftIndent =
      parentIndent !== undefined ? parentIndent : anchorParagraph.leftIndent;
    newParagraph.firstLineIndent = 0;
    insertPlainText(newParagraph, text, Word.InsertLocation.replace);
    await context.sync();
    return newParagraph;
  }

  const newParagraph = anchorParagraph.insertParagraph("", insertLocation);
  await context.sync();

  newParagraph.load("isListItem");
  await context.sync();

  if (newParagraph.isListItem) {
    if (safeListLevel !== undefined) {
      newParagraph.listItem.level = safeListLevel;
      await context.sync();
    } else {
      const targetIndent = anchorParagraph.leftIndent;
      newParagraph.detachFromList();
      newParagraph.leftIndent = targetIndent;
      await context.sync();
    }
  } else if (safeListLevel !== undefined) {
    const prevPara = newParagraph.getPreviousOrNullObject();
    prevPara.load("isListItem");
    await context.sync();
    if (!prevPara.isNullObject && prevPara.isListItem) {
      prevPara.listOrNullObject.load("id");
      await context.sync();
      if (!prevPara.listOrNullObject.isNullObject) {
        try {
          newParagraph.attachToList(
            prevPara.listOrNullObject.id,
            safeListLevel,
          );
          await context.sync();
        } catch (error) {
          console.warn(
            "Could not attach inserted clause to adjacent list, leaving as plain text:",
            error,
          );
        }
      }
    }
  }

  insertPlainText(newParagraph, text, Word.InsertLocation.replace);
  await context.sync();

  return newParagraph;
}

function preserveFormatting(originalText: string, newText: string): string {
  const cleanNewText = newText.trim();

  if (originalText.includes("\n") && cleanNewText.includes("\n")) {
    const originalLines = originalText.split("\n");
    const newLines = cleanNewText.split("\n");

    const indentations = originalLines.map(line => {
      const match = line.match(/^(\s*)/);
      return match ? match[1] : "";
    });

    const formattedLines = newLines.map((line, index) => {
      const cleanLine = line.trim();
      if (cleanLine === "") {
        return "";
      }

      const indent =
        index < indentations.length
          ? indentations[index]
          : indentations[indentations.length - 1] || "";

      return indent + cleanLine;
    });

    return (
      formattedLines.join("\n") + (originalText.endsWith("\n") ? "\n" : "")
    );
  } else {
    const leadingMatch = originalText.match(/^(\s*)/);
    const leadingWhitespace = leadingMatch ? leadingMatch[1] : "";
    return leadingWhitespace + cleanNewText;
  }
}
